/**
 * Microphone selection and audio level monitoring hook.
 *
 * Provides:
 * - List of available audio input devices
 * - Selected device ID with setter
 * - Real-time audio level (0–100) for a visual level meter
 *
 * PERMISSION HANDLING:
 * We call getUserMedia exactly once with { audio: true } to get blanket mic
 * permission. This stream is kept and used directly for the default mic.
 * When the user switches to a different mic, we call getUserMedia with
 * { deviceId: selectedId } (without "exact") — the browser reuses the
 * existing permission grant and doesn't prompt again.
 */

import { useState, useEffect, useRef } from "react";

interface UseMicrophoneResult {
  devices: MediaDeviceInfo[];
  selectedId: string;
  setSelectedId: (id: string) => void;
  level: number;
  isReady: boolean;
  /** True once we've detected actual audio input from the mic */
  hasDetectedAudio: boolean;
  /** True once the user has granted microphone permission */
  permissionGranted: boolean;
  /** Stop all mic streams and audio processing. Call before handing the mic to the GameLift SDK. */
  cleanup: () => void;
}

export function useMicrophone(): UseMicrophoneResult {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [level, setLevel] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [hasDetectedAudio, setHasDetectedAudio] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);

  // Refs for cleanup
  const audioCtxRef = useRef<AudioContext | null>(null);
  const animFrameRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);
  // Track the initial stream so we can reuse it for the default device
  const initialStreamRef = useRef<MediaStream | null>(null);

  // Step 1: Request mic permission once, enumerate devices, auto-select default.
  // The initial getUserMedia stream is kept alive and reused for level monitoring.
  useEffect(() => {
    async function init() {
      try {
        // Single permission request — no deviceId constraint = browser picks default
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        initialStreamRef.current = stream;
        setPermissionGranted(true);

        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const mics = allDevices.filter((d) => d.kind === "audioinput");
        setDevices(mics);

        if (mics.length > 0) {
          // Figure out which device the browser gave us so we can match it
          const track = stream.getAudioTracks()[0];
          const settings = track.getSettings();
          const defaultId = settings.deviceId ?? mics[0].deviceId;
          setSelectedId(defaultId);
        }
      } catch (err) {
        console.error("Failed to enumerate microphones:", err);
      }
    }
    init();

    return () => {
      // Stop initial stream on unmount if it hasn't been consumed
      if (initialStreamRef.current) {
        initialStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // Step 2: When the selected device changes, set up level monitoring.
  // Reuses the initial stream for the default device; creates a new one only
  // when the user switches devices (without "exact" to avoid re-prompting).
  useEffect(() => {
    if (!selectedId) return;

    let cancelled = false;

    // Reset audio detection when switching devices
    setHasDetectedAudio(false);

    async function startMonitoring() {
      // Clean up previous monitoring
      cancelAnimationFrame(animFrameRef.current);
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
      // Stop previous stream (but not the initial one — it gets reused or replaced)
      if (streamRef.current && streamRef.current !== initialStreamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }

      try {
        let stream: MediaStream;

        // Reuse the initial stream if it matches the selected device
        if (initialStreamRef.current) {
          const track = initialStreamRef.current.getAudioTracks()[0];
          const settings = track.getSettings();
          if (settings.deviceId === selectedId) {
            stream = initialStreamRef.current;
          } else {
            // Different device — stop initial stream and get a new one.
            // Using deviceId without "exact" reuses the permission grant.
            initialStreamRef.current.getTracks().forEach((t) => t.stop());
            initialStreamRef.current = null;
            stream = await navigator.mediaDevices.getUserMedia({
              audio: { deviceId: selectedId },
            });
          }
        } else {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: selectedId },
          });
        }

        if (cancelled) {
          if (stream !== initialStreamRef.current) {
            stream.getTracks().forEach((t) => t.stop());
          }
          return;
        }
        streamRef.current = stream;

        // Web Audio API pipeline: MediaStream → AnalyserNode
        // Chrome may create the context in "suspended" state if no user gesture
        // has happened yet. Explicitly resume it to ensure audio data flows.
        const ctx = new AudioContext();
        if (ctx.state === "suspended") {
          await ctx.resume();
        }
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        function tick() {
          if (cancelled) return;
          analyser.getByteFrequencyData(dataArray);
          const avg =
            dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
          const normalized = Math.round((avg / 255) * 100);
          setLevel(normalized);
          // Consider audio "detected" once level exceeds a small threshold
          if (normalized > 3) setHasDetectedAudio(true);
          animFrameRef.current = requestAnimationFrame(tick);
        }
        tick();

        setIsReady(true);
      } catch (err) {
        console.error("Failed to open microphone:", err);
        setIsReady(false);
        setHasDetectedAudio(false);
      }
    }

    startMonitoring();

    return () => {
      cancelled = true;
      cancelAnimationFrame(animFrameRef.current);
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
      // Don't stop streamRef here — it may be reused on next selectedId change
    };
  }, [selectedId]);

  // Stop all streams on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  function cleanup() {
    cancelAnimationFrame(animFrameRef.current);
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (initialStreamRef.current) {
      initialStreamRef.current.getTracks().forEach((t) => t.stop());
      initialStreamRef.current = null;
    }
  }

  return { devices, selectedId, setSelectedId, level, isReady, hasDetectedAudio, permissionGranted, cleanup };
}
